import { isApiError, unwrap, type OtpRequestResult, type TokenPair } from '@qrkey/api-client';
import { loginIdentifierSchema, otpCodeSchema, recoveryCodeSchema } from '@qrkey/contracts';
import { Alert, Button, TextField } from '@qrkey/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { api, tokens } from '../app/api.js';
import { isTillRole, terminalCredential, useTerminal } from '../app/terminal.js';
import { errorMessage } from '../lib/errors.js';
import { AuthLayout } from './AuthLayout.js';

type Step = 'credentials' | 'totp' | 'company';

interface Company {
  id: string;
  name: string;
}

/** Only same-app paths are followed after sign-in. */
export function safeRedirect(value: unknown): string | null {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : null;
}

export function useFinishSignIn(redirect?: string) {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  return (pair: TokenPair) => {
    queryClient.clear();
    tokens.set(pair);
    if (pair.session.totpEnrolmentRequired) {
      void navigate({ to: '/setup/totp' });
      return;
    }
    const target = safeRedirect(redirect);
    if (target) router.history.push(target);
    else void navigate({ to: '/' });
  };
}

export function SignInScreen({
  method,
  redirect,
}: {
  method: 'password' | 'otp';
  redirect?: string;
}) {
  const terminal = useTerminal();
  const finish = useFinishSignIn(redirect);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [otpSent, setOtpSent] = useState<OtpRequestResult | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [step, setStep] = useState<Step>('credentials');
  const [second, setSecond] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [onTill, setOnTill] = useState(terminal ? isTillRole(terminal.role) : false);
  const [error, setError] = useState<string>();
  const [fieldError, setFieldError] = useState<{ field: string; message: string }>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const bindTerminal = terminal && (onTill || !isTillRole(terminal.role));

  async function signIn(companyId?: string) {
    const extras = {
      ...(step === 'totp' || second
        ? useRecovery
          ? { recoveryCode: second.trim().toLowerCase() }
          : { totpCode: second.trim() }
        : {}),
      ...(companyId ? { companyId } : {}),
      ...(bindTerminal ? { terminal: terminalCredential(terminal) } : {}),
    };
    setBusy(true);
    setError(undefined);
    try {
      const pair =
        method === 'password'
          ? await unwrap(
              api.client.POST('/v1/auth/login', { body: { identifier, password, ...extras } }),
            )
          : await unwrap(
              api.client.POST('/v1/auth/otp/verify', {
                body: { identifier, code: code.trim(), ...extras },
              }),
            );
      finish(pair);
    } catch (e) {
      if (isApiError(e, 'TOTP_REQUIRED')) {
        if (step === 'totp') setError('Enter the current code from your authenticator app.');
        setStep('totp');
      } else if (isApiError(e, 'COMPANY_SELECTION_REQUIRED')) {
        const list = (e.problem as { companies?: Company[] }).companies ?? [];
        setCompanies(list);
        setStep('company');
      } else {
        setError(errorMessage(e, method === 'otp' && step === 'credentials' ? 'otp' : 'password'));
        if (step === 'totp') setSecond('');
      }
    } finally {
      setBusy(false);
    }
  }

  async function requestCode() {
    setBusy(true);
    setError(undefined);
    try {
      const sent = await unwrap(api.client.POST('/v1/auth/otp/request', { body: { identifier } }));
      setOtpSent(sent);
      setResendIn(sent.resendAfterSeconds);
    } catch (e) {
      setError(errorMessage(e, 'otp'));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldError(undefined);
    if (step === 'credentials') {
      if (!loginIdentifierSchema.safeParse(identifier.trim()).success) {
        setFieldError({ field: 'identifier', message: 'Enter a phone number or an email address' });
        return;
      }
      if (method === 'password' && password.length === 0) {
        setFieldError({ field: 'password', message: 'Enter your password' });
        return;
      }
      if (method === 'otp' && !otpSent) {
        void requestCode();
        return;
      }
      if (method === 'otp' && !otpCodeSchema.safeParse(code.trim()).success) {
        setFieldError({ field: 'code', message: 'Enter the 6-digit code' });
        return;
      }
    }
    if (step === 'totp') {
      const ok = useRecovery
        ? recoveryCodeSchema.safeParse(second.trim().toLowerCase()).success
        : otpCodeSchema.safeParse(second.trim()).success;
      if (!ok) {
        setFieldError({
          field: 'second',
          message: useRecovery
            ? 'Enter a recovery code like abcde-fghjk'
            : 'Enter the 6-digit code',
        });
        return;
      }
    }
    void signIn();
  }

  const fe = (field: string) => (fieldError?.field === field ? fieldError.message : undefined);

  if (step === 'company') {
    return (
      <AuthLayout title="Choose a company" description="You work for more than one company.">
        <div className="stack">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          {companies.map((c) => (
            <Button
              key={c.id}
              variant="secondary"
              size="lg"
              block
              loading={busy}
              onClick={() => void signIn(c.id)}
            >
              {c.name}
            </Button>
          ))}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={step === 'totp' ? 'Two-step verification' : 'Sign in'}
      description={
        step === 'totp'
          ? useRecovery
            ? 'Enter one of your recovery codes. Each one works once.'
            : 'Enter the 6-digit code from your authenticator app.'
          : method === 'password'
            ? 'Use your email or phone number and password.'
            : 'We will send a one-time code to your email or phone.'
      }
    >
      <form className="stack" onSubmit={onSubmit} noValidate>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {step === 'credentials' ? (
          <>
            <TextField
              label="Email or phone"
              name="identifier"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setOtpSent(null);
              }}
              error={fe('identifier')}
              autoFocus
              inputSize="lg"
            />
            {method === 'password' ? (
              <TextField
                label="Password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={fe('password')}
                inputSize="lg"
              />
            ) : null}
            {method === 'otp' && otpSent ? (
              <>
                <Alert tone="success">
                  If an account exists for {identifier}, a code is on its way by{' '}
                  {otpSent.channel === 'sms' ? 'SMS' : 'email'}. It works for{' '}
                  {Math.round(otpSent.expiresInSeconds / 60)} minutes.
                </Alert>
                <TextField
                  label="Sign-in code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  error={fe('code')}
                  autoFocus
                  inputSize="lg"
                  className="qk-num code-input"
                />
                <Button
                  variant="ghost"
                  onClick={() => void requestCode()}
                  disabled={resendIn > 0 || busy}
                >
                  {resendIn > 0 ? `Send a new code in ${resendIn} s` : 'Send a new code'}
                </Button>
              </>
            ) : null}
            {terminal && isTillRole(terminal.role) ? (
              <label className="qk-toggle">
                <input
                  type="checkbox"
                  checked={onTill}
                  onChange={(e) => setOnTill(e.target.checked)}
                />
                <span className="qk-toggle__text">
                  <span className="qk-toggle__name">Sign in to this till</span>
                  <span className="qk-toggle__hint">
                    Till permissions only. Untick for the back office on this device.
                  </span>
                </span>
              </label>
            ) : null}
          </>
        ) : (
          <TextField
            key={useRecovery ? 'recovery' : 'totp'}
            label={useRecovery ? 'Recovery code' : 'Authenticator code'}
            name="second"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoCapitalize="none"
            maxLength={useRecovery ? 11 : 6}
            value={second}
            onChange={(e) =>
              setSecond(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ''))
            }
            error={fe('second')}
            autoFocus
            inputSize="lg"
            className="qk-num code-input"
          />
        )}
        <Button type="submit" size="lg" block loading={busy}>
          {step === 'credentials' && method === 'otp' && !otpSent ? 'Send code' : 'Sign in'}
        </Button>
        <div className="auth__links">
          {step === 'totp' ? (
            <>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setSecond('');
                  setFieldError(undefined);
                }}
              >
                {useRecovery ? 'Use the authenticator app' : 'Use a recovery code'}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setStep('credentials');
                  setSecond('');
                  setError(undefined);
                }}
              >
                Back
              </button>
            </>
          ) : method === 'password' ? (
            <Link to="/login/code" search={redirect ? { redirect } : {}}>
              Sign in with a one-time code
            </Link>
          ) : (
            <Link to="/login" search={redirect ? { redirect } : {}}>
              Sign in with a password
            </Link>
          )}
          {terminal && isTillRole(terminal.role) && step === 'credentials' ? (
            <Link to="/pin">Sign in with a PIN</Link>
          ) : null}
        </div>
      </form>
    </AuthLayout>
  );
}
